import HeaderCell from '@components/UI-Components/HierarchyWiseItemsTable/components/Utils/HeaderCell'
import withAlignment, {
  Alignment
} from '@components/UI-Components/HierarchyWiseItemsTable/components/Utils/Alignment'
import { VerticalAlignment } from '@components/UI-Components/HierarchyWiseItemsTable/components/Utils/Alignment/Alignment'
import {
  Column,
  ColumnType
} from '@components/UI-Components/HierarchyWiseItemsTable/models/column'
import { EnhancedKPIViewKeyMap, KPIViewKeyMap } from '@modules/order/constants/keys'
import cloneDeep from 'lodash/cloneDeep'
import ViewSettingStore from '../../ViewSettingStore'
import EnhancedHeirarchyKPIValue from '@modules/showroom/EnhancedOrder/components/EnhancedKPI/EnhancedHeirarchyKPIValue'
import EnhancedMixTarget from '@modules/showroom/EnhancedOrder/components/EnhancedKPI/EnhancedMixTarget'
import EnhancedHeirarchyName from '@modules/showroom/EnhancedOrder/components/EnhancedKPI/EnhancedHeirarchyName'
import * as css from '@modules/order/stores/view/List/./ListViewTableService.css'
import { OrderType } from '@api/common'

export enum KPITableView {
  Overview = 'Overview',
  DoorsKPI = 'DoorsKPI',
  Buy = 'Buy',
  Forecast = 'Forecast'
}

export default class EnhancedKPIViewTableService extends ViewSettingStore {
  private commonHeaderCellElement = withAlignment(
    HeaderCell,
    Alignment.CENTER,
    VerticalAlignment.END,
    css['enhancedKPI-cellAlignment']
  )
  private fixedColumns = [
    {
      field: 'name',
      label: 'Activity/ Family/ Line',
      key: 'activityFamilyLine',
      allowHide: false,
      fixed: true,
      allowReordering: false,
      showBorders: true,
      isStickyBorder: true,
      hierarchyRowCellTemplate: withAlignment(
        EnhancedHeirarchyName,
        Alignment.LEFT,
        VerticalAlignment.CENTER
      ),
      headerCellElement: this.commonHeaderCellElement,
      width: 300
    }
  ] as Column[]

  private getKPIColumns = quantityType => {
    // Wholesale KPI columns
    return [
      {
        label: 'Actual/ Target Mix Quantity',
        allowHide: true,
        key: 'kpis.actualTargetMixQuantity',
        allowReordering: true,
        showBorders: true,
        hierarchyRowCellTemplate: EnhancedMixTarget,
        width: 200,
        field: `kpis.${KPIViewKeyMap[quantityType]?.actualMixQuantity}`,
        enhancedField: `${EnhancedKPIViewKeyMap[quantityType]?.actualMixQuantity}`,
        targetMixField: 'mixOfBudgetQuantity',
        headerCellElement: this.commonHeaderCellElement
      },
      {
        label: 'Actual/ Target Mix Value',
        allowHide: true,
        key: 'kpis.actualTargetMixValue',
        allowReordering: true,
        showBorders: true,
        hierarchyRowCellTemplate: EnhancedMixTarget,
        width: 200,
        field: `kpis.${KPIViewKeyMap[quantityType]?.actualMixValue}`,
        enhancedField: `${EnhancedKPIViewKeyMap[quantityType]?.actualMixValue}`,
        targetMixField: 'mixOfBuyerBudgetValue',
        headerCellElement: this.commonHeaderCellElement
      },
      {
        label: 'Quantity',
        allowHide: true,
        key: 'kpis.quantity',
        allowReordering: true,
        showBorders: true,
        hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
        width: 150,
        field: `kpis.${KPIViewKeyMap[quantityType]?.totalQuantity}`,
        enhancedField: `${EnhancedKPIViewKeyMap[quantityType]?.totalQuantity}`,
        headerCellElement: this.commonHeaderCellElement
      },
      {
        label: 'Value',
        allowHide: true,
        key: 'kpis.value',
        allowReordering: true,
        showBorders: true,
        hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
        width: 150,
        field: `kpis.${KPIViewKeyMap[quantityType]?.totalValue}`,
        enhancedField: `${EnhancedKPIViewKeyMap[quantityType]?.totalValue}`,
        headerCellElement: this.commonHeaderCellElement
      }
    ]
  }

  private getRetailKPIColumns = (subSection: string, quantityType: string) => {
    // Retail KPI common columns
    const isForcastTab = subSection === KPITableView.Forecast
    const avgWeekSales = {
      label: 'Avg Week Sales',
      allowHide: true,
      key: 'kpis.aws',
      showBorders: true,
      allowReordering: true,
      hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
      width: 150,
      field: 'kpis.aws',
      enhancedField: isForcastTab
        ? 'forecastAverageWeeklySales'
        : `${EnhancedKPIViewKeyMap[quantityType]?.averageWeeklySales}`,
      headerCellElement: this.commonHeaderCellElement
    }
    const avgDepth = {
      label: 'Avg Depth',
      allowHide: true,
      key: 'kpis.averageDepth',
      showBorders: true,
      allowReordering: true,
      hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
      width: 150,
      field: 'kpis.averageDepth',
      enhancedField: isForcastTab
        ? 'forecastAverageDepth'
        : `${EnhancedKPIViewKeyMap[quantityType]?.averageDepth}`,
      headerCellElement: this.commonHeaderCellElement
    }
    const skuEfficiency = {
      label: 'SKU Eff',
      allowHide: true,
      key: 'kpis.skuEfficiency',
      showBorders: true,
      allowReordering: true,
      hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
      width: 150,
      field: 'kpis.skuEfficiency',
      enhancedField: isForcastTab
        ? 'forecastSkuEff'
        : `${EnhancedKPIViewKeyMap[quantityType]?.skuEff}`,
      headerCellElement: this.commonHeaderCellElement
    }
    const numberOfStores = {
      label: 'Total Stores',
      allowHide: true,
      key: 'kpis.numberOfStores',
      showBorders: true,
      allowReordering: true,
      hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
      width: 150,
      field: 'kpis.numberOfStores',
      enhancedField: isForcastTab ? 'forecastNumberOfStores' : 'numberOfStores',
      headerCellElement: this.commonHeaderCellElement
    }
    const salesQty = {
      label: 'Sales Qty',
      allowHide: true,
      key: 'kpis.salesQty',
      showBorders: true,
      allowReordering: true,
      hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
      width: 150,
      field: 'kpis.salesQty',
      enhancedField: isForcastTab
        ? 'forecastSalesQuantity'
        : `${EnhancedKPIViewKeyMap[quantityType]?.salesQuantity}`,
      headerCellElement: this.commonHeaderCellElement
    }
    return subSection === KPITableView.Forecast
      ? ([
          avgWeekSales,
          {
            label: 'Mix Qty',
            allowHide: true,
            key: 'kpis.mixRatioByDraftQty',
            allowReordering: true,
            showBorders: true,
            hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
            width: 150,
            field: 'kpis.mixRatioByDraftQty',
            enhancedField: 'mixOfForecastQuantity',
            headerCellElement: this.commonHeaderCellElement
          },
          {
            label: 'Mix Value',
            allowHide: true,
            key: 'kpis.mixRatioByDraftValue',
            allowReordering: true,
            showBorders: true,
            hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
            width: 150,
            field: 'kpis.mixRatioByDraftValue',
            enhancedField: 'mixOfForecastValue',
            headerCellElement: this.commonHeaderCellElement
          },
          avgDepth,
          skuEfficiency,
          numberOfStores,
          {
            label: 'Forecast Value',
            allowHide: true,
            key: 'kpis.totalForecastValue',
            showBorders: true,
            allowReordering: true,
            hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
            width: 150,
            field: 'kpis.totalForecastValue',
            enhancedField: 'totalForecastValue',
            headerCellElement: this.commonHeaderCellElement
          },
          salesQty,
          {
            label: 'Forecast Qty',
            allowHide: true,
            key: 'kpis.totalForecastQuantity',
            showBorders: true,
            allowReordering: true,
            hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
            width: 150,
            field: 'kpis.totalForecastQuantity',
            enhancedField: 'totalForecastQuantity',
            headerCellElement: this.commonHeaderCellElement
          }
        ] as Column[])
      : ([
          numberOfStores,
          {
            label: 'Forecast Qty',
            allowHide: true,
            key: 'kpis.totalForecastQuantity',
            showBorders: true,
            allowReordering: true,
            hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
            width: 150,
            field: 'kpis.totalForecastQuantity',
            enhancedField: 'totalForecastQuantity',
            headerCellElement: this.commonHeaderCellElement
          },
          {
            label: 'Order Qty',
            allowHide: true,
            key: 'kpis.totalBuyQuantity',
            showBorders: true,
            allowReordering: true,
            hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
            width: 150,
            field: 'kpis.totalBuyQuantity',
            enhancedField: `${EnhancedKPIViewKeyMap[quantityType]?.totalQuantity}`,
            headerCellElement: this.commonHeaderCellElement
          },
          avgWeekSales,
          avgDepth,
          skuEfficiency,
          {
            label: 'Order Value',
            allowHide: true,
            key: 'kpis.totalBuyValue',
            showBorders: true,
            allowReordering: true,
            hierarchyRowCellTemplate: EnhancedHeirarchyKPIValue,
            width: 150,
            field: 'kpis.totalBuyValue',
            enhancedField: `${EnhancedKPIViewKeyMap[quantityType]?.totalValue}`,
            headerCellElement: this.commonHeaderCellElement
          },
          salesQty
        ] as Column[])
  }

  getColumns(
    doors: any[],
    quantityType,
    isRetailClient = false,
    subSection = '',
    orderType?
  ) {
    return isRetailClient
      ? ([
          ...this.fixedColumns,
          ...this.getRetailKPIColumns(subSection, quantityType)
        ] as Column[])
      : ([
          ...this.fixedColumns,
          ...(doors?.length
            ? doors.map(
                (door, index) =>
                  ({
                    label: door.name,
                    allowHide: true,
                    allowReordering: true,
                    showBorders: true,
                    type: ColumnType.Grouped,
                    doorIndex: index,
                    hideDivider:
                      orderType === OrderType.Client &&
                      subSection === KPITableView.DoorsKPI,
                    field: `kpis.doorKpis.${door.id}`,
                    columns: cloneDeep(this.getKPIColumns(quantityType)).map(col => {
                      col['allowReordering'] = false
                      col['field'] = `kpis.doorKpis.${door.id}.${col.field
                        .split('.')
                        .splice(1, 1)}`
                      col['doorId'] = door?.id
                      return col
                    })
                  } as Column)
              )
            : this.getKPIColumns(quantityType))
        ] as Column[])
  }
}
